import { useMemo, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Download,
  ExternalLink,
  ShieldAlert,
} from "lucide-react";
import { Button } from "@/components/atoms/studio/ui/button";

interface Finding {
  rule_id: string;
  severity: string;
  domain: string;
  target: string;
  evidence: string;
  line: number | null;
  fix_summary: string;
  runs_skill: string | null;
}

interface RuleReport {
  rule_id: string;
  title: string;
  description: string;
  severity: string;
  domain: string;
  type: string;
  sf_doc_url: string | null;
  fix_summary: string;
  runs_skill: string | null;
  auto_remediable: boolean;
  finding_count: number;
  findings: Finding[];
  error: string | null;
}

export interface ReleaseScanSummary {
  release_id: string;
  name: string;
  release_date: string | null;
  critical_deadline: string | null;
  status: string;
  scanned_rules: number;
  total_findings: number;
  findings_by_severity: Record<string, number>;
  findings_by_domain: Record<string, number>;
  rules: RuleReport[];
}

type ScanEvent =
  | {
      type: "scan_start";
      release_id: string;
      name: string;
      release_date: string | null;
      critical_deadline: string | null;
      status: string;
      total_rules: number;
    }
  | { type: "rule_done"; rule: RuleReport; index: number; total: number }
  | {
      type: "scan_done";
      scanned_rules: number;
      total_findings: number;
      findings_by_severity: Record<string, number>;
      findings_by_domain: Record<string, number>;
    };

export function parseReleaseScanSse(content: string): ReleaseScanSummary | null {
  if (!content.includes("event: scan_start") || !content.includes("event: scan_done")) {
    return null;
  }

  const events: ScanEvent[] = [];
  for (const line of content.split(/\r?\n/)) {
    const trimmed = line.trim();
    if (!trimmed.startsWith("data:")) continue;
    const payload = trimmed.slice(5).trim();
    if (!payload.startsWith("{")) continue;
    try {
      const parsed = JSON.parse(payload) as ScanEvent;
      if (parsed?.type === "scan_start" || parsed?.type === "rule_done" || parsed?.type === "scan_done") {
        events.push(parsed);
      }
    } catch {
      // Skip malformed stream lines. The report can still render from valid frames.
    }
  }

  const start = events.find((event): event is Extract<ScanEvent, { type: "scan_start" }> => event.type === "scan_start");
  const done = [...events].reverse().find((event): event is Extract<ScanEvent, { type: "scan_done" }> => event.type === "scan_done");
  if (!start || !done) return null;

  const rules = events
    .filter((event): event is Extract<ScanEvent, { type: "rule_done" }> => event.type === "rule_done")
    .map((event) => event.rule);

  return {
    release_id: start.release_id,
    name: start.name,
    release_date: start.release_date,
    critical_deadline: start.critical_deadline,
    status: start.status,
    scanned_rules: done.scanned_rules,
    total_findings: done.total_findings,
    findings_by_severity: done.findings_by_severity,
    findings_by_domain: done.findings_by_domain,
    rules,
  };
}

export function ReleaseScanReport({ report }: { report: ReleaseScanSummary }) {
  const sortedRules = useMemo(() => sortRules(report.rules), [report.rules]);
  const criticalRules = sortedRules.filter((rule) => rule.finding_count > 0 && rule.severity === "critical");
  const highRules = sortedRules.filter((rule) => rule.finding_count > 0 && rule.severity === "high");

  return (
    <div className="space-y-4 text-foreground">
      <div className="rounded-lg border border-border bg-card p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <ShieldAlert className="h-5 w-5 text-orange-500" />
              <h2 className="text-xl font-semibold">{report.name} Release Readiness</h2>
            </div>
            <p className="mt-1 text-sm text-muted-foreground">
              {report.scanned_rules} rules scanned. {report.total_findings} findings need review before {report.critical_deadline ?? "the release deadline"}.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <DownloadButton filename={`release_scan_${report.release_id}.json`} mime="application/json" content={JSON.stringify(report, null, 2)}>
              JSON
            </DownloadButton>
            <DownloadButton filename={`release_scan_${report.release_id}.csv`} mime="text/csv" content={reportToCsv(report)}>
              CSV
            </DownloadButton>
            <DownloadButton filename={`release_scan_${report.release_id}.md`} mime="text/markdown" content={reportToMarkdown(report)}>
              Markdown
            </DownloadButton>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-5">
        <SeverityTile label="Critical" value={report.findings_by_severity.critical ?? 0} tone="critical" />
        <SeverityTile label="High" value={report.findings_by_severity.high ?? 0} tone="high" />
        <SeverityTile label="Medium" value={report.findings_by_severity.medium ?? 0} tone="medium" />
        <SeverityTile label="Low" value={report.findings_by_severity.low ?? 0} tone="low" />
        <SeverityTile label="Clean Rules" value={report.rules.filter((rule) => rule.finding_count === 0).length} tone="clean" />
      </div>

      <div className="grid gap-3 lg:grid-cols-3">
        <ImpactPanel
          title="Fix First"
          value={criticalRules.length}
          detail={criticalRules.length ? criticalRules.map((rule) => rule.title).slice(0, 3).join(", ") : "No critical rules with findings."}
          tone="critical"
        />
        <ImpactPanel
          title="Plan Next"
          value={highRules.length}
          detail={highRules.length ? highRules.map((rule) => rule.title).slice(0, 3).join(", ") : "No high-severity rules with findings."}
          tone="high"
        />
        <ImpactPanel
          title="Ready"
          value={report.rules.filter((rule) => rule.finding_count === 0).length}
          detail="Rules with no detected impact in this org."
          tone="clean"
        />
      </div>

      <div className="space-y-3">
        {sortedRules.map((rule) => (
          <ReleaseRuleCard key={rule.rule_id} rule={rule} />
        ))}
      </div>
    </div>
  );
}

function ReleaseRuleCard({ rule }: { rule: RuleReport }) {
  const [open, setOpen] = useState(rule.finding_count > 0 && (rule.severity === "critical" || rule.severity === "high"));
  const hasFindings = rule.finding_count > 0;

  return (
    <div className="overflow-y-hidden rounded-lg border border-border bg-card">
      <button
        type="button"
        className="flex w-full items-center gap-3 p-4 text-left transition hover:bg-muted/30"
        onClick={() => setOpen((value) => !value)}
      >
        <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase ${severityClass(rule.severity)}`}>
          {rule.severity}
        </span>
        <div className="min-w-0 flex-1">
          <div className="truncate font-semibold">{rule.title}</div>
          <div className="mt-0.5 flex flex-wrap gap-x-2 text-xs text-muted-foreground">
            <span>{rule.domain}</span>
            <span>{rule.type}</span>
            <span>{rule.rule_id}</span>
          </div>
        </div>
        <span className={`rounded border px-2 py-1 text-xs ${hasFindings ? "border-orange-500/40 text-orange-400" : "border-border text-muted-foreground"}`}>
          {rule.finding_count} {rule.finding_count === 1 ? "finding" : "findings"}
        </span>
        {open ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
      </button>

      {open ? (
        <div className="space-y-3 border-t border-border p-4">
          {rule.description ? <p className="whitespace-pre-line text-sm text-muted-foreground">{rule.description}</p> : null}
          {rule.error ? (
            <p className="rounded border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300">
              <AlertTriangle className="mr-1 inline h-4 w-4" />
              Detector issue: {rule.error}
            </p>
          ) : null}
          {!rule.error && !hasFindings ? (
            <p className="text-sm text-muted-foreground">
              <CheckCircle2 className="mr-1 inline h-4 w-4 text-green-500" />
              No impact detected for this rule.
            </p>
          ) : null}
          {hasFindings ? <FindingsTable findings={rule.findings} /> : null}
          <div className="rounded border-l-4 border-blue-500/70 bg-muted/30 p-3 text-sm">
            <span className="font-semibold">Recommended fix: </span>
            {rule.fix_summary || "Review this rule and plan the remediation."}
            {rule.runs_skill ? <span> Run `{rule.runs_skill}` for guided remediation.</span> : null}
            {rule.sf_doc_url ? (
              <a href={rule.sf_doc_url} target="_blank" rel="noreferrer" className="ml-2 inline-flex items-center gap-1 text-primary hover:underline">
                Salesforce docs <ExternalLink className="h-3 w-3" />
              </a>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function FindingsTable({ findings }: { findings: Finding[] }) {
  return (
    <div className="overflow-x-auto rounded border border-border">
      <table className="w-full min-w-[720px] text-sm">
        <thead className="bg-muted/40 text-[11px] uppercase text-muted-foreground">
          <tr>
            <th className="p-2 text-left">Target</th>
            <th className="p-2 text-left">Evidence</th>
            <th className="p-2 text-left">Fix</th>
          </tr>
        </thead>
        <tbody>
          {findings.slice(0, 100).map((finding, index) => (
            <tr key={`${finding.rule_id}-${finding.target}-${index}`} className="border-t border-border">
              <td className="p-2 font-mono text-xs">{finding.target}</td>
              <td className="p-2 font-mono text-xs text-muted-foreground">{finding.evidence || "Detected by rule."}</td>
              <td className="p-2 text-xs text-muted-foreground">{finding.fix_summary || "Review and remediate."}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {findings.length > 100 ? (
        <div className="border-t border-border p-2 text-xs text-muted-foreground">
          {findings.length - 100} more findings are included in the CSV and JSON downloads.
        </div>
      ) : null}
    </div>
  );
}

function SeverityTile({ label, value, tone }: { label: string; value: number; tone: "critical" | "high" | "medium" | "low" | "clean" }) {
  return (
    <div className="rounded-lg border border-border bg-card p-3">
      <div className="text-[11px] font-semibold uppercase text-muted-foreground">{label}</div>
      <div className={`mt-1 text-3xl font-bold ${toneTextClass(tone)}`}>{value}</div>
    </div>
  );
}

function ImpactPanel({ title, value, detail, tone }: { title: string; value: number; detail: string; tone: "critical" | "high" | "clean" }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="text-[11px] font-semibold uppercase text-muted-foreground">{title}</div>
      <div className={`mt-1 text-2xl font-bold ${toneTextClass(tone)}`}>{value}</div>
      <p className="mt-2 text-sm text-muted-foreground">{detail}</p>
    </div>
  );
}

function DownloadButton({ filename, mime, content, children }: { filename: string; mime: string; content: string; children: React.ReactNode }) {
  return (
    <Button type="button" variant="outline" size="sm" onClick={() => downloadBlob(filename, content, mime)}>
      <Download className="mr-1 h-3.5 w-3.5" />
      {children}
    </Button>
  );
}

function downloadBlob(filename: string, content: string, mime: string) {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

function sortRules(rules: RuleReport[]) {
  const order: Record<string, number> = { critical: 0, high: 1, medium: 2, low: 3, info: 4 };
  return [...rules].sort((a, b) => {
    const severity = (order[a.severity] ?? 9) - (order[b.severity] ?? 9);
    if (severity !== 0) return severity;
    return b.finding_count - a.finding_count;
  });
}

function severityClass(severity: string) {
  if (severity === "critical") return "border-red-500/40 bg-red-500/10 text-red-500";
  if (severity === "high") return "border-orange-500/40 bg-orange-500/10 text-orange-500";
  if (severity === "medium") return "border-yellow-500/40 bg-yellow-500/10 text-yellow-500";
  if (severity === "low") return "border-zinc-500/40 bg-zinc-500/10 text-zinc-400";
  return "border-green-500/40 bg-green-500/10 text-green-500";
}

function toneTextClass(tone: "critical" | "high" | "medium" | "low" | "clean") {
  if (tone === "critical") return "text-red-500";
  if (tone === "high") return "text-orange-500";
  if (tone === "medium") return "text-yellow-500";
  if (tone === "low") return "text-zinc-400";
  return "text-green-500";
}

function reportToCsv(report: ReleaseScanSummary): string {
  const rows = ["severity,domain,rule_id,target,evidence,fix_summary"];
  for (const rule of report.rules) {
    for (const finding of rule.findings) {
      rows.push(
        [
          finding.severity,
          finding.domain,
          finding.rule_id,
          finding.target,
          finding.evidence || "",
          finding.fix_summary || rule.fix_summary || "",
        ]
          .map((cell) => `"${String(cell).replace(/"/g, '""')}"`)
          .join(",")
      );
    }
  }
  return rows.join("\n");
}

function reportToMarkdown(report: ReleaseScanSummary): string {
  const lines = [
    `# ${report.name} Release Readiness`,
    "",
    `Release: ${report.release_id}`,
    `Status: ${report.status}`,
    `Release date: ${report.release_date ?? "n/a"}`,
    `Critical deadline: ${report.critical_deadline ?? "n/a"}`,
    "",
    `Scanned rules: ${report.scanned_rules}`,
    `Total findings: ${report.total_findings}`,
    "",
    "## Severity Summary",
    "",
    `- Critical: ${report.findings_by_severity.critical ?? 0}`,
    `- High: ${report.findings_by_severity.high ?? 0}`,
    `- Medium: ${report.findings_by_severity.medium ?? 0}`,
    `- Low: ${report.findings_by_severity.low ?? 0}`,
    "",
    "## Rule Findings",
    "",
  ];

  for (const rule of sortRules(report.rules)) {
    lines.push(`### ${rule.title}`, "");
    lines.push(`Severity: ${rule.severity}`);
    lines.push(`Domain: ${rule.domain}`);
    lines.push(`Findings: ${rule.finding_count}`);
    lines.push(`Fix: ${rule.fix_summary || "Review and remediate."}`);
    lines.push("");
    for (const finding of rule.findings.slice(0, 25)) {
      lines.push(`- ${finding.target}: ${finding.evidence || "Detected by rule."}`);
    }
    if (rule.findings.length > 25) {
      lines.push(`- ${rule.findings.length - 25} more findings in CSV/JSON.`);
    }
    lines.push("");
  }

  return lines.join("\n");
}
